import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  Combobox,
  useCombobox,
  InputBase,
  PillsInput,
  Pill,
  CloseButton,
  Input,
  Group,
  Checkbox,
  Loader,
} from '@mantine/core';
import {
  useRepositoryOptions,
  type UseRepositoryOptionsParams,
} from '@ayocore/platform/client/hooks/useRepositoryOptions';
import type { RepoItem, RpcRepoClient } from '@ayocore/platform/server/rpc/types';
import classes from './RepositoryCombobox.module.css';

/** Кастомные элементы дропдауна. Слоты — общий примитив; конкретные политики
 *  (напр. чекбокс «Без X») собираются слоем выше (createRepositoryFilter). */
export interface RepositoryComboboxSlots {
  /** Над списком, до поиска (липкий заголовок). */
  top?: ReactNode;
  /** Под списком (липкий футер). */
  bottom?: ReactNode;
  /** Первым элементом списка опций. */
  firstItem?: ReactNode;
  /** Последним элементом списка опций. */
  lastItem?: ReactNode;
}

type SharedProps<R extends RpcRepoClient> = Omit<UseRepositoryOptionsParams<R>, 'repo'> & {
  repo: R;
  placeholder?: string;
  size?: string;
  disabled?: boolean;
  /** Кнопка очистки (single) / возможность снять все пиллы (multi). */
  clearable?: boolean;
  /**
   * multi: максимум пиллов в поле; сверх — компактный счётчик «+K» вместо
   * горизонтального/вертикального разрастания (как у Mantine MultiSelect).
   * Скрытые значения снимаются через чекбоксы в дропдауне. По умолчанию —
   * показывать все.
   */
  maxDisplayedValues?: number;
  /** Серверный поиск в дропдауне (по умолчанию true). */
  searchable?: boolean;
  searchPlaceholder?: string;
  nothingFoundMessage?: string;
  maxDropdownHeight?: number;
  /** Ширина таргета (инпута/контрола). */
  width?: number | string;
  /**
   * Ширина дропдауна. По умолчанию = ширине таргета (как у Mantine Combobox).
   * - число/строка (`300`, `'20rem'`) — фиксированная ширина, раскрывашка шире/уже инпута;
   * - `'max-content'` / `'auto'` — подстроиться под самый длинный пункт (не обрезать лейблы),
   *   с защитным максимумом `dropdownMaxWidth`, чтобы не уехать за экран.
   */
  dropdownWidth?: number | string;
  /**
   * Верхний предел ширины дропдауна при `dropdownWidth='max-content'` (иначе игнорируется).
   * По умолчанию `min(360px, 90vw)`.
   */
  dropdownMaxWidth?: number | string;
  style?: CSSProperties;
  slots?: RepositoryComboboxSlots;
  /**
   * Доп. опции НЕ из репозитория, закреплённые сверху списка (напр. «Без X» /
   * «Не назначен» / «Все»). Текут через обычный `value`/`onChange` и показываются
   * как обычные пиллы/выбор; засеваются в кэш лейблов. Скрываются при активном
   * текстовом поиске. XOR-семантику (если нужна) обеспечивает потребитель в `onChange`.
   */
  staticOptions?: { value: string; label: string }[];
  /**
   * Кастомный рендер опции в дропдауне (мини-панель вместо строкового лейбла).
   * Получает исходную запись репозитория. Если не задан — рендерится `getOptionLabel`.
   * На пиллы/выбранное значение НЕ влияет (там по-прежнему строковый лейбл) и на
   * staticOptions не распространяется (у них лейбл — строка).
   */
  renderOption?: (item: RepoItem<R>) => ReactNode;
  /** Открыть дропдаун сразу при монтировании (напр. когда компонент монтируется
   *  по клику на внешний триггер — ссылку/значение — вместо своего постоянного инпута). */
  autoOpen?: boolean;
  /** Дропдаун закрылся (клик снаружи, Escape, выбор в single-режиме). Не вызывается
   *  при размонтировании компонента — только при реальном закрытии Mantine Combobox. */
  onDropdownClose?: () => void;
};

type SingleProps<R extends RpcRepoClient> = SharedProps<R> & {
  mode?: 'single';
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiProps<R extends RpcRepoClient> = SharedProps<R> & {
  mode: 'multi';
  value: string[];
  onChange: (value: string[]) => void;
};

export type RepositoryComboboxProps<R extends RpcRepoClient> = SingleProps<R> | MultiProps<R>;

/**
 * RepositoryCombobox — репозиторный select (выпадающий список / dropdown) на
 * низкоуровневом Mantine Combobox: грузит опции из RPC-репозитория, серверный поиск,
 * режимы single и multi (multi-select с пиллами).
 *
 * - режимы `single` (value: string|null) / `multi` (value: string[], пиллы + «+K»),
 *   дискриминированный union по `mode`;
 * - загрузка/серверный поиск/маппинг — через общее ядро {@link useRepositoryOptions};
 * - слоты top/firstItem/lastItem/bottom для кастомного контента дропдауна;
 * - `clearable` (× очистки), `maxDisplayedValues` (коллапс пиллов в «+K»),
 *   `staticOptions` (доп. опции не из репозитория, закреплённые сверху — напр. «Без X»);
 * - лейблы выбранных значений вне загруженной страницы дотягиваются по id автоматически
 *   (ensureValues в ядре) — выбранный из URL элемент показывает имя, а не голый id.
 *
 * Чистый presentational-виджет: НЕ знает про URL/фильтры таблицы.
 *
 * Когда использовать:
 * - select по репозиторию в ФОРМЕ/диалоге, где нужен multi-выбор и/или слоты в дропдауне.
 *
 * Когда НЕ использовать:
 * - фильтр таблицы → {@link createRepositoryFilter} (оборачивает этот виджет + URL + «Без X»);
 * - простой single-select без слотов → RepositorySelect (обёртка над Mantine Select).
 *
 * @example
 * // multi-выбор пользователей в форме
 * <RepositoryCombobox
 *   mode='multi'
 *   repo={useUserRepository().onlyHumanActive().orderByName('asc')}
 *   displayField='name'
 *   value={ids}
 *   onChange={setIds}
 *   maxDisplayedValues={1}
 *   placeholder='Участники...'
 * />
 *
 * @see createRepositoryFilter — обёртка для select-фильтров RepositoryTable
 * @see useRepositoryOptions — переиспользуемое ядро загрузки опций
 */
export function RepositoryCombobox<R extends RpcRepoClient>(props: RepositoryComboboxProps<R>) {
  const {
    repo,
    placeholder,
    size = 'xs',
    disabled,
    clearable = true,
    maxDisplayedValues,
    searchable = true,
    searchPlaceholder = 'Поиск...',
    nothingFoundMessage = 'Ничего не найдено',
    maxDropdownHeight = 240,
    width,
    dropdownWidth,
    dropdownMaxWidth,
    style,
    slots,
    staticOptions,
    displayField,
    valueField,
    getOptionLabel,
    getOptionValue,
    customFilter,
    enableServerSearch,
    initialLimit,
    searchLimit,
    searchDebounce,
    minSearchLength,
    initialOption,
    autoOpen,
    onDropdownClose,
  } = props;

  const isMulti = props.mode === 'multi';

  // Выбранные значения — нужны до хука, чтобы дотянуть их лейблы (ensureValues).
  const selectedValues: string[] =
    props.mode === 'multi' ? props.value : props.value ? [props.value] : [];
  const selectedSet = new Set(selectedValues);
  // Лейблы выбранных значений (кроме staticOptions — у них лейбл уже есть) дотягиваем по id.
  const ensureValues = useMemo(() => {
    const staticVals = new Set((staticOptions ?? []).map(o => o.value));
    return selectedValues.filter(v => !staticVals.has(v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues.join('|'), staticOptions]);

  const { options, ensuredOptions, loading, search, setSearch, hasServerSearch } =
    useRepositoryOptions<R>({
      repo,
      displayField,
      valueField,
      getOptionLabel,
      getOptionValue,
      customFilter,
      enableServerSearch,
      initialLimit,
      searchLimit,
      searchDebounce,
      minSearchLength,
      initialOption,
      ensureValues,
    });

  const combobox = useCombobox({
    onDropdownOpen: () => {
      // Сразу ставим фокус в поиск дропдауна (для обоих режимов поиск там)
      if (searchable) combobox.focusSearchInput();
    },
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      // Возвращаем список к исходному состоянию после закрытия
      if (hasServerSearch && search !== '') setSearch('');
      onDropdownClose?.();
    },
  });

  // Открыть дропдаун сразу при монтировании — когда компонент показан по клику
  // на внешний триггер (ссылку/значение), а не по клику на свой инпут.
  useEffect(() => {
    if (autoOpen) combobox.openDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Кэш лейблов для выбранных значений — чтобы пиллы/выбор не теряли подпись,
  // когда запись выпала из текущей выдачи (серверный поиск сузил список).
  const [labelMap, setLabelMap] = useState<Record<string, string>>({});
  useEffect(() => {
    setLabelMap(prev => {
      let changed = false;
      const next = { ...prev };
      for (const o of options) {
        if (next[o.value] !== o.label) {
          next[o.value] = o.label;
          changed = true;
        }
      }
      // staticOptions тоже засеваем — иначе их пилл показал бы сырое value
      for (const o of staticOptions ?? []) {
        if (next[o.value] !== o.label) {
          next[o.value] = o.label;
          changed = true;
        }
      }
      // ensuredOptions — лейблы выбранных значений, дотянутые по id вне страницы
      for (const o of ensuredOptions) {
        if (next[o.value] !== o.label) {
          next[o.value] = o.label;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [options, staticOptions, ensuredOptions]);
  const labelFor = (value: string) => labelMap[value] ?? value;

  const emitSingle = (value: string | null) => {
    if (props.mode !== 'multi') props.onChange(value);
  };
  const emitMulti = (values: string[]) => {
    if (props.mode === 'multi') props.onChange(values);
  };

  const handleOptionSubmit = (value: string) => {
    if (isMulti) {
      const next = new Set(selectedSet);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      emitMulti([...next]);
    } else {
      emitSingle(value);
      combobox.closeDropdown();
    }
  };

  // Общий рендер пункта списка (репозиторные и staticOptions выглядят одинаково).
  const renderOptionRow = (value: string, label: string, item?: RepoItem<R>) => {
    const selected = selectedSet.has(value);
    const custom = props.renderOption && item !== undefined ? props.renderOption(item) : null;
    return (
      <Combobox.Option
        key={value}
        value={value}
        active={selected}
        classNames={{ option: classes.option }}
      >
        <Group gap='xs' wrap='nowrap' align={custom ? 'flex-start' : 'center'}>
          {isMulti && <Checkbox checked={selected} readOnly tabIndex={-1} size='xs' aria-hidden />}
          {custom ? (
            <div style={{ flex: 1, minWidth: 0 }}>{custom}</div>
          ) : (
            <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
          )}
        </Group>
      </Combobox.Option>
    );
  };

  // staticOptions показываем только когда нет активного текстового поиска.
  const staticVisible = (staticOptions?.length ?? 0) > 0 && search === '';

  // Авто-ширина дропдауна по контенту: 'max-content'/'auto' сайзят раскрывашку
  // под самый длинный пункт; защитный maxWidth не даёт уехать за экран.
  const isAutoDropdown = dropdownWidth === 'max-content' || dropdownWidth === 'auto';
  const dropdownStyle: CSSProperties | undefined = isAutoDropdown
    ? { maxWidth: dropdownMaxWidth ?? 'min(360px, 90vw)' }
    : undefined;

  const targetStyle: CSSProperties = { width, ...style };

  // --- Target ---
  const singleValue = props.mode !== 'multi' ? props.value : null;

  // Коллапс пиллов: показываем первые maxDisplayedValues, остальные сворачиваем в «+K».
  const isCollapsed = maxDisplayedValues != null && selectedValues.length > maxDisplayedValues;
  const visibleValues = isCollapsed ? selectedValues.slice(0, maxDisplayedValues) : selectedValues;
  const hiddenCount = selectedValues.length - visibleValues.length;

  const target = isMulti ? (
    <PillsInput
      size={size}
      disabled={disabled}
      pointer
      onClick={() => combobox.openDropdown()}
      rightSection={
        clearable && selectedValues.length > 0 && !disabled ? (
          <CloseButton
            size={size}
            aria-label='Clear'
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation();
              emitMulti([]);
            }}
          />
        ) : (
          <Combobox.Chevron />
        )
      }
      rightSectionPointerEvents={
        clearable && selectedValues.length > 0 && !disabled ? 'all' : 'none'
      }
      style={targetStyle}
    >
      <Pill.Group
        style={
          // Однострочный режим только при включённом коллапсе; иначе — обычный перенос
          maxDisplayedValues != null ? { flexWrap: 'nowrap', overflow: 'hidden' } : undefined
        }
      >
        {selectedValues.length === 0 && <Input.Placeholder>{placeholder}</Input.Placeholder>}
        {visibleValues.map(value => (
          <Pill
            key={value}
            withRemoveButton={clearable && !disabled}
            onRemove={() => emitMulti(selectedValues.filter(v => v !== value))}
            // Mantine Pill по умолчанию `flex: 0` (база 0%, до контента дотягивает
            // min-width:auto). Переопределяем на `0 1 auto` + minWidth:0 — идиома
            // «обрезка по многоточию»: есть место → полное имя; нет → ellipsis
            // (у Pill.label уже text-overflow:ellipsis), а не толкает «+K» на 2-ю строку.
            style={{ flex: '0 1 auto', minWidth: 0 }}
          >
            {labelFor(value)}
          </Pill>
        ))}
        {/* `0 0 auto` — счётчик всегда по контенту, не сжимается и не ужимается */}
        {hiddenCount > 0 && <Pill style={{ flex: '0 0 auto' }}>+{hiddenCount}</Pill>}
        {/* Поля ввода в таргете НЕТ намеренно: единственный инпут — поиск в дропдауне
            (Combobox.Search), он же events-target (стрелки/Enter/Escape через
            useComboboxTargetProps). Два инпута дрались бы за фокус. */}
      </Pill.Group>
    </PillsInput>
  ) : (
    <InputBase
      component='button'
      type='button'
      size={size}
      pointer
      disabled={disabled}
      onClick={() => combobox.toggleDropdown()}
      rightSection={
        clearable && singleValue && !disabled ? (
          <CloseButton
            size={size}
            aria-label='Clear'
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation();
              emitSingle(null);
            }}
          />
        ) : (
          <Combobox.Chevron />
        )
      }
      rightSectionPointerEvents={clearable && singleValue && !disabled ? 'all' : 'none'}
      style={targetStyle}
    >
      {/* display:block обязателен: Mantine input — block/width:100%, а у inline-span
          overflow/ellipsis не работают. Block занимает контент-бокс (минус место под
          шеврон/×) и обрезает длинное значение многоточием вместо наезда «поверх инпута». */}
      <span
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'left',
        }}
      >
        {singleValue ? labelFor(singleValue) : <Input.Placeholder>{placeholder}</Input.Placeholder>}
      </span>
    </InputBase>
  );

  return (
    // size прокидываем в Combobox — иначе опции/поиск берут дефолтный (крупный)
    // размер шрифта, не совпадая с size таргета и чекбоксом «Без X»
    <Combobox
      store={combobox}
      size={size}
      width={dropdownWidth}
      // bottom-start — левый край дропдауна совпадает с левым краем таргета (а не
      // центрируется), чтобы более широкий дропдаун рос вправо, а не «в обе стороны»
      position='bottom-start'
      onOptionSubmit={handleOptionSubmit}
      withinPortal
    >
      {/* multi: DropdownTarget только позиционирует дропдаун; клавиатуру/события несёт
          Combobox.Search внутри дропдауна. single: совмещённый Target на кнопке. */}
      {isMulti ? (
        <Combobox.DropdownTarget>{target}</Combobox.DropdownTarget>
      ) : (
        <Combobox.Target>{target}</Combobox.Target>
      )}

      <Combobox.Dropdown style={dropdownStyle}>
        {slots?.top}

        {/* Поиск в дропдауне для обоих режимов (в multi поле-таргет скрыто). */}
        {searchable && (
          <Combobox.Search
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            placeholder={searchPlaceholder}
            rightSection={loading ? <Loader size='xs' /> : undefined}
          />
        )}

        <Combobox.Options mah={maxDropdownHeight} style={{ overflowY: 'auto' }}>
          {slots?.firstItem}

          {/* Опции не из репозитория, закреплённые сверху списка (напр. «Без X») */}
          {staticVisible && staticOptions?.map(o => renderOptionRow(o.value, o.label))}

          {loading && options.length === 0 && <Combobox.Empty>Загрузка…</Combobox.Empty>}
          {!loading && options.length === 0 && !staticVisible && (
            <Combobox.Empty>{nothingFoundMessage}</Combobox.Empty>
          )}

          {options.map(o => renderOptionRow(o.value, o.label, o.item))}

          {slots?.lastItem}
        </Combobox.Options>

        {slots?.bottom}
      </Combobox.Dropdown>
    </Combobox>
  );
}

export default RepositoryCombobox;
